import {
  Dialog,
  DialogContent,
  Typography,
  Chip,
  IconButton,
} from "@mui/material";
import { Box } from "@mui/system";
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import CircularProgress from "@mui/material/CircularProgress";
import CardTransactions from "./CardTransactions";
import RefreshingIndicator from "./RefreshingIndicator";
import PendingIcon from "@mui/icons-material/Pending";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

export default function MemberTransactions(props) {
  const { memberInfo } = props;
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pageInfoEndCursor, setPageInfoEndCursor] = useState("");
  const [pageInfoStartCursor, setPageInfoStartCursor] = useState("");
  const [pageCursorMap, setPageCursorMap] = useState({});
  const [statusFilter, setStatusFilter] = useState("POSTED");

  async function fetchCardTransactions(page, pageSize, statusFilter) {
    console.log("executing fetchCardTransactions");
    const dateTo = new Date();
    dateTo.setDate(dateTo.getDate() + 1); // Add one day to dateTo
    const dateFrom = new Date(
      dateTo.getFullYear(),
      dateTo.getMonth(),
      dateTo.getDate() - 87
    );

    const dateToFormatted = dateTo.toISOString().split("T")[0];
    const dateFromFormatted = dateFrom.toISOString().split("T")[0];

    const response = await HttpService.getAxiosClient().get(
      window.API_URL +
        `/member/card-transactions?memberId=` +
        memberInfo.memberId +
        `&companyId=` +
        memberInfo.companyId +
        `&dateFrom=` +
        dateFromFormatted +
        `&dateTo=` +
        dateToFormatted +
        `&page=` +
        page +
        `&pageSize=` +
        pageSize +
        `&pageInfoEndCursor=` +
        pageInfoEndCursor +
        `&status=` +
        statusFilter
    );

    setPageCursorMap((prev) => ({
      ...prev,
      [page]: {
        pageInfoEndCursor: response.data.pageInfoEndCursor,
        pageInfoStartCursor: response.data.pageInfoStartCursor,
      },
    }));
    return response;
  }

  async function fetchCardTransactionsCount(statusFilter) {
    const dateTo = new Date();
    dateTo.setDate(dateTo.getDate() + 1); // Add one day to dateTo
    const dateFrom = new Date(
      dateTo.getFullYear(),
      dateTo.getMonth(),
      dateTo.getDate() - 87
    );

    const dateToFormatted = dateTo.toISOString().split("T")[0];
    const dateFromFormatted = dateFrom.toISOString().split("T")[0];
    if (pageInfoEndCursor === "" || pageInfoEndCursor === undefined) {
      const response = await HttpService.getAxiosClient().get(
        window.API_URL +
          `/member/card-transactions/count?memberId=` +
          memberInfo.memberId +
          `&companyId=` +
          memberInfo.companyId +
          `&dateFrom=` +
          dateFromFormatted +
          `&dateTo=` +
          dateToFormatted +
          `&status=` +
          statusFilter
      );
      return response;
    } else {
      return 1000;
    }
  }

  const {
    data: dataCardTransactions,
    isError: isErrorCardTransactions,
    isFetching: isFetchingCardTransactions,
    isLoading: isLoadingCardTransactions,
  } = useQuery(
    ["cardTransactions", page, pageSize, statusFilter, memberInfo.memberId],
    () => fetchCardTransactions(page, pageSize, statusFilter),
    {
      keepPreviousData: true,
      enabled: !!memberInfo?.memberId, // Ensure memberInfo is defined before running the query
    }
  );

  const {
    data: dataCardTransactionsCount,
    isError: isErrorCardTransactionsCount,
    isFetching: isFetchingCardTransactionsCount,
    isLoading: isLoadingCardTransactionsCount,
  } = useQuery(
    ["cardTransactionsCount", statusFilter, memberInfo.memberId],
    () => fetchCardTransactionsCount(statusFilter),
    {
      keepPreviousData: true,
      enabled: !!memberInfo?.memberId, // Ensure memberInfo is defined before running the query
    }
  );

  const handleClose = (event, reason) => {
    if (reason === "backdropClick") return; //avoid closing the modal by clicking the backdrop
    props.setOpen(false);
  };

  return (
    <Dialog
      maxWidth="100%"
      open={props.open}
      onClose={props.handleClose}
      disableEscapeKeyDown={true}
      fullScreen
      PaperProps={{
        style: {
          width: "90%",
          height: "90%",
          margin: "auto",
        },
      }}
    >
      <Box component="form" noValidate autoComplete="off">
        {/* Title bar with same style as MemberDetail */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" component="div" color="white">
              Card Transactions
            </Typography>
            <ReceiptLongIcon sx={{ color: "white" }} />
          </Box>
          <Box>
            <IconButton
              edge="end"
              color="inherit"
              size="small"
              onClick={handleClose}
              aria-label="close"
            >
              <CloseIcon sx={{ color: "white" }} />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ width: "95%", flexShrink: 0 }}>
          {isLoadingCardTransactions || isLoadingCardTransactions ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "50vh",
                color: "primary",
              }}
            >
              <CircularProgress color="primary" />
            </div>
          ) : isErrorCardTransactions || isErrorCardTransactionsCount ? (
            <h4>There was an error getting the info</h4>
          ) : (
            <>
              <div style={{ height: "2rem" }}>
                {isFetchingCardTransactions ||
                isFetchingCardTransactionsCount ? (
                  <RefreshingIndicator />
                ) : (
                  ""
                )}
              </div>
              <div
                style={{
                  paddingBottom: "1rem",
                  display: "flex",
                  alignContent: "space-between",
                }}
              >
                <Chip
                  style={{ width: "10rem", marginRight: "1rem" }}
                  onClick={() => {
                    setStatusFilter("POSTED");
                  }}
                  icon={<CheckCircleOutlineIcon color="primary" />}
                  label="Posted"
                  variant={statusFilter === "POSTED" ? "none" : "outlined"}
                  sx={
                    statusFilter === "POSTED"
                      ? { fontWeight: "bold", border: "1px solid black" }
                      : {}
                  }
                />
                <Chip
                  style={{ width: "10rem", marginRight: "1rem" }}
                  onClick={() => {
                    setStatusFilter("PENDING");
                  }}
                  icon={<PendingIcon color="secondary" />}
                  label="Pending"
                  variant={statusFilter === "PENDING" ? "none" : "outlined"}
                  sx={
                    statusFilter === "PENDING"
                      ? { fontWeight: "bold", border: "1px solid black" }
                      : {}
                  }
                />
                <Chip
                  style={{ width: "10rem", marginRight: "1rem" }}
                  onClick={() => {
                    setStatusFilter("AUTHORIZED");
                  }}
                  icon={<CheckBoxIcon color="secondary" />}
                  label="Authorized"
                  variant={statusFilter === "AUTHORIZED" ? "none" : "outlined"}
                  sx={
                    statusFilter === "AUTHORIZED"
                      ? { fontWeight: "bold", border: "1px solid black" }
                      : {}
                  }
                />
                <Chip
                  style={{ width: "10rem", marginRight: "1rem" }}
                  onClick={() => {
                    setStatusFilter("DENIED");
                  }}
                  icon={<DoDisturbIcon color="error" />}
                  label="Declined"
                  variant={statusFilter === "DENIED" ? "none" : "outlined"}
                  sx={
                    statusFilter === "DENIED"
                      ? { fontWeight: "bold", border: "1px solid black" }
                      : {}
                  }
                />
              </div>
              <CardTransactions
                isLoading={isLoadingCardTransactions}
                isError={isErrorCardTransactions}
                data={dataCardTransactions}
                dataCount={dataCardTransactionsCount}
                isFetchingDataCount={isLoadingCardTransactionsCount}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                setPage={setPage}
                setPageSize={setPageSize}
                setPageInfoEndCursor={setPageInfoEndCursor}
                setPageInfoStartCursor={setPageInfoStartCursor}
                pageInfoStartCursor={pageInfoStartCursor}
                pageInfoEndCursor={pageInfoEndCursor}
                pageSize={pageSize}
                page={page}
                pageCursorMap={pageCursorMap}
              />
            </>
          )}

          <br />

          <br />
        </DialogContent>
      </Box>
    </Dialog>
  );
}
